import { useEffect, useRef, useState, type RefObject } from 'react';

interface Options {
  /** Async refresh handler. The hook waits for it before retracting. */
  onRefresh: () => Promise<void> | void;
  /** Distance (px) the user must drag before refresh triggers. Default 70. */
  threshold?: number;
  /** Hard cap on visual pull distance. Default 120. */
  maxPull?: number;
  /** Optional disable flag (e.g. while another refresh is running). */
  disabled?: boolean;
}

interface State {
  /** Current visual pull distance in px (0 when idle). */
  distance: number;
  /** True while the user is actively dragging past the threshold. */
  armed: boolean;
  /** True while the onRefresh promise is pending. */
  refreshing: boolean;
}

/**
 * Lightweight pull-to-refresh for any scroll container (defaults to window).
 *
 * Only triggers when scrollTop is at 0 AND the user drags down — avoiding
 * conflicts with normal scrolling. Uses pointer events so it works on both
 * touch and trackpad-with-touch surfaces. Visual feedback is left to the
 * caller via the returned `distance` (in px) and state flags.
 */
export function usePullToRefresh(
  ref: RefObject<HTMLElement | null> | null,
  opts: Options
): State {
  const { onRefresh, threshold = 70, maxPull = 120, disabled = false } = opts;
  const [distance, setDistance] = useState(0);
  const [armed, setArmed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // refs so handlers don't capture stale state
  const startYRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (disabled) return;
    const target: HTMLElement | Window = ref?.current ?? window;

    const isAtTop = (): boolean => {
      if (target === window) {
        return (window.scrollY ?? document.documentElement.scrollTop) <= 0;
      }
      return (target as HTMLElement).scrollTop <= 0;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (!isAtTop()) return;
      const touch = e.touches[0];
      if (!touch) return;
      startYRef.current = touch.clientY;
      draggingRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (refreshing) return;
      if (startYRef.current === null) return;
      if (!isAtTop()) {
        startYRef.current = null;
        return;
      }
      const touch = e.touches[0];
      if (!touch) return;
      const delta = touch.clientY - startYRef.current;
      if (delta <= 0) return;
      // resistance: square-root falloff so the bar doesn't fly to the bottom
      const eased = Math.min(maxPull, Math.sqrt(delta) * 8);
      draggingRef.current = true;
      setDistance(eased);
      setArmed(eased >= threshold);
      // prevent overscroll bounce competing with our visual
      if (delta > 4 && e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (refreshing) return;
      const wasArmed = armed;
      const wasDragging = draggingRef.current;
      startYRef.current = null;
      draggingRef.current = false;
      if (!wasDragging) {
        setDistance(0);
        setArmed(false);
        return;
      }
      if (wasArmed) {
        setRefreshing(true);
        setDistance(threshold);
        try {
          await onRefreshRef.current();
        } finally {
          setRefreshing(false);
          setDistance(0);
          setArmed(false);
        }
      } else {
        setDistance(0);
        setArmed(false);
      }
    };

    // touch handlers — passive must be false on touchmove so we can preventDefault
    const moveOpts: AddEventListenerOptions = { passive: false };
    target.addEventListener('touchstart', onTouchStart as EventListener, { passive: true });
    target.addEventListener('touchmove', onTouchMove as EventListener, moveOpts);
    target.addEventListener('touchend', onTouchEnd as EventListener, { passive: true });
    target.addEventListener('touchcancel', onTouchEnd as EventListener, { passive: true });

    return () => {
      target.removeEventListener('touchstart', onTouchStart as EventListener);
      target.removeEventListener('touchmove', onTouchMove as EventListener);
      target.removeEventListener('touchend', onTouchEnd as EventListener);
      target.removeEventListener('touchcancel', onTouchEnd as EventListener);
    };
  }, [ref, disabled, refreshing, armed, threshold, maxPull]);

  return { distance, armed, refreshing };
}
